/*********************************************************************************************
 * Program Side Panel Manager
 * Gère l'ouverture/fermeture des panneaux latéraux des programmes santé
 * @version 1.0.0
 */

(function() {
  'use strict';

  // Éviter la double initialisation
  if (window.mmProgramSidePanelInit) return;
  window.mmProgramSidePanelInit = true;

  /*********************************************************************************************
   * Constants
   */
  const SELECTORS = {
    panel: '[data-panel-id]',
    openTrigger: '[data-open-program-panel]',
    closeButton: '[data-panel-close]',
    addToCartForm: '[data-type="add-to-cart-form"]',
    focusable: 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  };

  const CLASSES = {
    active: 'mm-active',
    closing: 'mm-closing'
  };

  const ANIMATION_DURATION = 300;

  /*********************************************************************************************
   * State Management
   */
  const state = {
    currentTrigger: null,
    focusTrapHandlers: new Map()
  };

  /*********************************************************************************************
   * Helper Functions
   */

  /**
   * Trouve un panneau par son ID
   * @param {string} panelId - L'ID du panneau
   * @returns {HTMLElement|null}
   */
  function findPanel(panelId) {
    return document.querySelector(`${SELECTORS.panel}[data-panel-id="${panelId}"]`);
  }

  /**
   * Trouve tous les éléments focusables dans un conteneur
   * @param {HTMLElement} container
   * @returns {HTMLElement[]}
   */
  function getFocusableElements(container) {
    return Array.from(container.querySelectorAll(SELECTORS.focusable));
  }

  /**
   * Bloque le scroll de la page
   */
  function lockScroll() {
    document.body.style.overflow = 'hidden';
  }

  /**
   * Restaure le scroll de la page
   */
  function unlockScroll() {
    document.body.style.overflow = '';
  }

  /*********************************************************************************************
   * Focus Trap Management
   */

  /**
   * Configure le piège de focus pour un panneau
   * @param {HTMLElement} panel
   * @param {HTMLElement} triggerElement
   */
  function setupFocusTrap(panel, triggerElement) {
    const focusableElements = getFocusableElements(panel);

    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    state.currentTrigger = triggerElement;

    const trapHandler = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Tab + Shift : focus sur le dernier élément si on est sur le premier
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab : focus sur le premier élément si on est sur le dernier
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    panel.addEventListener('keydown', trapHandler);
    state.focusTrapHandlers.set(panel, trapHandler);

    // Focus sur le premier élément focusable
    requestAnimationFrame(() => firstFocusable.focus());
  }

  /**
   * Supprime le piège de focus d'un panneau
   * @param {HTMLElement} panel
   */
  function removeFocusTrap(panel) {
    const handler = state.focusTrapHandlers.get(panel);

    if (handler) {
      panel.removeEventListener('keydown', handler);
      state.focusTrapHandlers.delete(panel);
    }
  }

  /*********************************************************************************************
   * Panel Operations
   */

  /**
   * Ouvre un panneau
   * @param {string} panelId
   * @param {HTMLElement} triggerElement
   */
  function openPanel(panelId, triggerElement) {
    const panel = findPanel(panelId);
    if (!panel) return;

    // Retirer l'attribut hidden
    if (panel.hasAttribute('hidden')) {
      panel.removeAttribute('hidden');
    }

    // Mettre à jour aria-expanded sur le trigger
    if (triggerElement) {
      triggerElement.setAttribute('aria-expanded', 'true');
    }

    // Animation d'ouverture
    requestAnimationFrame(() => {
      void panel.offsetHeight; // Force reflow
      panel.classList.add(CLASSES.active);
      lockScroll();
      setupFocusTrap(panel, triggerElement);
    });
  }

  /**
   * Ferme un panneau
   * @param {string} panelId
   */
  function closePanel(panelId) {
    const panel = findPanel(panelId);
    if (!panel) return;

    // Mettre à jour aria-expanded sur le trigger
    if (state.currentTrigger) {
      state.currentTrigger.setAttribute('aria-expanded', 'false');
    }

    // Animation de fermeture
    panel.classList.remove(CLASSES.active);
    panel.classList.add(CLASSES.closing);
    removeFocusTrap(panel);

    // Nettoyage après animation
    setTimeout(() => {
      panel.classList.remove(CLASSES.closing);
      panel.setAttribute('hidden', '');
      unlockScroll();

      // Retour du focus sur le trigger
      if (state.currentTrigger) {
        state.currentTrigger.focus();
        state.currentTrigger = null;
      }
    }, ANIMATION_DURATION);
  }

  /*********************************************************************************************
   * Event Handlers
   */

  /**
   * Gère les clics sur la page
   * @param {Event} e
   */
  function handleClick(e) {
    // Ouverture du panneau via le lien "Voir détail"
    const openTrigger = e.target.closest(SELECTORS.openTrigger);
    if (openTrigger) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      openPanel(openTrigger.dataset.openProgramPanel, openTrigger);
      return;
    }

    // Fermeture via le bouton close
    const closeButton = e.target.closest(SELECTORS.closeButton);
    if (closeButton) {
      e.preventDefault();
      closePanel(closeButton.dataset.panelClose);
      return;
    }

    // Fermeture via clic sur l'overlay
    const panel = e.target.closest(SELECTORS.panel);
    if (panel && e.target === panel) {
      closePanel(panel.dataset.panelId);
      return;
    }
  }

  /**
   * Gère la soumission du formulaire d'ajout au panier
   * @param {Event} e
   */
  function handleFormSubmit(e) {
    const form = e.target;

    // Vérifier si c'est un formulaire d'ajout au panier dans un panneau
    if (!form.matches(SELECTORS.addToCartForm)) return;

    const panel = form.closest(SELECTORS.panel);
    if (!panel) return;

    // Fermer le panneau après l'ajout au panier (après un court délai pour la confirmation)
    setTimeout(() => {
      closePanel(panel.dataset.panelId);
    }, 500);
  }

  /**
   * Gère la touche Échap
   * @param {KeyboardEvent} e
   */
  function handleKeydown(e) {
    if (e.key !== 'Escape') return;

    const activePanel = document.querySelector(`${SELECTORS.panel}.${CLASSES.active}`);
    if (activePanel) {
      closePanel(activePanel.dataset.panelId);
    }
  }

  /*********************************************************************************************
   * Initialization
   */
  document.addEventListener('click', handleClick, true);
  document.addEventListener('submit', handleFormSubmit);
  document.addEventListener('keydown', handleKeydown);

})();
